import { useCallback, useEffect, useState } from 'react';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IRecruitSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IRecruitGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox } from 'src/layouts/form/FormButton';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { recruitApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { RecruitFormDialog } from './RecruitFormDialog';

interface IRecruitGridProps {
  searchData: Partial<IRecruitSFilter>;
}

export const RecruitGrid = ({ searchData }: IRecruitGridProps) => {
  const { getRecruitGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IRecruitGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 목록 조회
  const getRecruitData = useCallback(async () => {
    const formData = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    await recruitApi.getRecruit(formData).then((res) => {
      if (res) {
        const newGridRows = res.data.map((recruit, i) => {
          return { ...recruit, id: recruit.recruitSid ?? i };
        });
        setGridRows(newGridRows);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [searchData, pagination.page, pagination.size]);

  useEffect(() => {
    getRecruitData();
  }, [getRecruitData]);

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IRecruitGridRow>) => {
    callApiWithLoading(async () => {
      await recruitApi.getDetail(param.row.recruitSid!).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.RECRUIT,
            formType: FormType.UPDATE,
            setOnClose: handleSubmitSuccess,
            dialogContent: (
              <RecruitFormDialog onSubmitSuccess={handleSubmitSuccess} selectedData={res.data} />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.RECRUIT);
    getRecruitData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    const newPagination = {
      ...pagination,
      page: paginationModel.page + 1, // MUI는 0-based, 서버는 1-based
      size: paginationModel.pageSize,
    };
    setPagination(newPagination);
  };

  return (
    <ContentBox>
      <CDataGridPro
        columns={getRecruitGridCol()}
        rows={gridRows}
        rowCount={toJS(pagination.totalElements)}
        paginationModel={{
          page: pagination.page - 1,
          pageSize: pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{ footer: CGridFooter }}
        onRowDoubleClick={handleRowDoubleClick}
      />
    </ContentBox>
  );
};

export default observer(RecruitGrid);
